import {
  getForestOwner,
  getForestOwners,
  verifyPropertyOwnership,
} from '@services/ForestOwners/ForestOwnerService.ts'
import { getOrganization, searchOrganizations } from '@services/OrganizationService.ts'
import {
  type GetHistoricalPropertiesParams,
  type GetPropertiesForForestOwnerParams,
  getHistoricalProperties,
  getPropertiesForForestOwner,
  getProperty,
  type PropertySearchParams,
  searchProperties,
} from '@services/PropertyService.ts'
import { getForestOwnerGroups } from '@services/ReferenceData/ForestOwnerGroupService.ts'
import { getPostalCodeCity } from '@services/ReferenceData/PostalCodeService.ts'
import { type QueryOptions, queryOptions } from '@tanstack/react-query'
import { mapDtoToProperties } from '@utils/property/mappers'
import { getCurrentTenant, type TenantId } from '@utils/tenant-utils'
import { z } from 'zod'
import { evaluateCooperativeMembershipWarning } from '@/features/CooperativeMembership/evaluateCooperativeMembershipWarning'
import type {
  GetContractorsParams,
  GetEmployeesParams,
  GetForestCrewsParams,
} from '@/types/Contractor'
import type { GetCooperativeMembershipParams } from '@/types/CooperativeMembership'
import type { GetForestOwnersParams } from '@/types/ForestOwners/ForestOwner'
import type { GetHistoricalEventsParams } from '@/types/ForestOwners/HistoricalEvents'
import type { GetSmsHistoryParams } from '@/types/ForestOwners/Sms'
import type {
  GetForestOwnerSupplierParams,
  GetSuppliersParams,
} from '@/types/ForestOwners/Supplier'
import type { GetContractorMachinesParams } from '@/types/Machine'
import type { GetOrganizationParams, SearchOrganizationParams } from '@/types/Organization.ts'
import type { Property, SearchPropertyParams, SearchPropertyResponse } from '@/types/Property'
import type {
  AssortmentCategorySort,
  GetSalesAgreementAssortmentCategoriesParams,
  GetSalesAgreementAssortmentPricesParams,
  GetSalesAgreementPriceListsParams,
  GetSalesAgreementPricePeriodParams,
  GetSalesAgreementPricePeriodsParams,
  GetSalesAgreementsParams,
  GetTimberBuyerDeliveryLocationsParams,
  GetTimberBuyerPricePeriodsParams,
  GetTimberBuyersParams,
  ZonePricesParams,
} from '@/types/TimberBuyer'
import { createLogger } from '@/utils/logger'
import { getAssortment, getAssortmentStatus, getAssortments } from './AssortmentService'
import { getAssortmentType, getAssortmentTypes } from './AssortmentTypeService'
import { getCompany, getSuppliersWithMunicipality } from './CompanyService'
import {
  type GetContractorDocumentsParams,
  getContractorDocuments,
} from './ContractorDocumentService'
import {
  findContractorByOrganizationNumber,
  type GetContractorSmsHistoryParams,
  getContractor,
  getContractorEmployees,
  getContractorForestCrews,
  getContractorSmsHistory,
  getContractorSmsSenders,
  getContractors,
  getCourses,
  getEmployee,
  getEmployees,
  getForestCrew,
  getForestCrews,
} from './ContractorService'
import {
  getCooperativeMembership,
  getPartnerCostPreview,
  getPartnerSettlementPreview,
  getShareholder,
} from './CooperativeMembershipService'
import { type GetDocumentsParams, getDocuments } from './DocumentService'
import { getAccountManagers } from './EmployeeService'
import { getOffices } from './EmployeesService'
import {
  getForestOwnerHistoricalEvents,
  getSmsHistory,
  getSmsSenders,
} from './ForestOwners/ActivityLogService'
import {
  getAdditionalInfoLines,
  getAdditionalInfoTypes,
} from './ForestOwners/AdditionalInfoService'
import { getForestOwnerSupplier, getSuppliers } from './ForestOwners/SuppliersService'
import { getLinesOfBusiness } from './LineOfBusinessService'

import { getContractorMachine, getContractorMachines, getMachineBrands } from './MachineService'

import { getMeasurementOrganizations } from './MeasurementOrganizationService'
import { getMunicipalities } from './MunicipalityService'
import { getPropertyOwners } from './OwnerService'
import { searchProperty } from './PropertyService'
import {
  getSalesAgreement,
  getSalesAgreementAssortmentCategories,
  getSalesAgreementAssortmentPrices,
  getSalesAgreementPriceLists,
  getSalesAgreementPricePeriod,
  getSalesAgreementPricePeriods,
  getSalesAgreements,
  getTimberBuyerPricePeriods,
  getZonePrices,
} from './SalesAgreements/SalesAgreementsService'
import {
  getTimberBuyer,
  getTimberBuyerDeliveryLocations,
  getTimberBuyers,
} from './TimberBuyerService'
import { getTransportPayers } from './TransportPayerService'
import { getTransportResponsibles } from './TransportResponsibleService'

const logger = createLogger('queryOptions')

export const organizationSearchBaseKey = ['organizations', 'search'] as const

export const contractorSearchSchema = z.object({
  searchTerm: z.string().optional(),
  sort: z.string().optional(),
})

export const timberBuyerSearchSchema = z.object({
  searchTerm: z.string().optional(),
  sort: z.string().optional(),
})

export const frameworkSearchSchema = z.object({
  searchTerm: z.string().optional(),
  sort: z.string().optional(),
})

export const forestOwnerSearchSchema = z.object({
  searchTerm: z.string().optional(),
  // Property search parameters - convert numbers to strings for URL compatibility
  municipality: z
    .union([z.string(), z.number()])
    .transform((val) => String(val))
    .optional(),
  gardsnummer: z
    .union([z.string(), z.number()])
    .transform((val) => String(val))
    .optional(),
  bruksnummer: z
    .union([z.string(), z.number()])
    .transform((val) => String(val))
    .optional(),
  advancedSearch: z.string().optional(),
  searchType: z.enum(['skogeier', 'eiendom', 'tømmerkjøper', 'entreprenør']).optional(),
  sort: z.string().optional(),
})

export const assortmentSearchSchema = z.object({
  searchTerm: z.string().optional(),
  sort: z.string().optional(),
})

export const getAssortmentsQuery = () =>
  queryOptions({
    queryKey: queryKeys.assortments(),
    queryFn: async () => {
      const [assortments, assortmentTypes] = await Promise.all([
        getAssortments(),
        getAssortmentTypes(),
      ])
      const typeNameById = new Map(assortmentTypes.map((type) => [type.id, type.name]))

      return assortments.map((assortment) =>
        assortment.typeNumber != null && typeNameById.has(assortment.typeNumber)
          ? { ...assortment, typeName: typeNameById.get(assortment.typeNumber) ?? null }
          : assortment,
      )
    },
  })

export const getAssortmentQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.assortment(id),
    queryFn: () => getAssortment(id),
  })

export const getAssortmentTypesQuery = () =>
  queryOptions({
    queryKey: queryKeys.assortmentTypes(),
    queryFn: getAssortmentTypes,
    select: (items) => items.filter(({ deleted }) => !deleted),
  })

export const getAssortmentTypeQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.assortmentType(id),
    queryFn: () => getAssortmentType(id),
  })

export const getAssortmentSyncStatusQuery = () =>
  queryOptions({
    queryKey: queryKeys.assortmentSyncStatus(),
    queryFn: getAssortmentStatus,
  })

/**
 * Creates a query config for fetching forest owners with TanStack Query
 * @param params Required query params: tenantId, pageSize, optional searchTerm, etc.
 */
export const getForestOwnersQuery = (params: GetForestOwnersParams) =>
  queryOptions({
    queryKey: [...forestOwnersBaseKey, params.tenantId ?? 'VikenSkog', params] as const,
    queryFn: () => getForestOwners(params),
  })

export const searchPropertiesQuery = (params: PropertySearchParams) =>
  queryOptions({
    queryKey: [...propertiesBaseKey, 'search', params] as const,
    queryFn: () => searchProperties(params),
  })

interface SearchPropertyWithExternalParams {
  municipality: string
  farmNumber: string
  propertyNumber?: string
  municipalityName?: string
  includeExternal?: boolean
}

/**
 * The searched number is only the main number when it is the root of its tree; otherwise the
 * external lookup returns the real root. Falls back to the searched number without one.
 */
const withExternalRoot = (searchedHnr: Property, externalHnr: Property | undefined): Property => {
  if (!externalHnr?.hnr) return searchedHnr

  const isSameMunicipality = externalHnr.municipality.number === searchedHnr.municipality.number

  return {
    ...searchedHnr,
    municipality: {
      // The provided name belongs to the searched municipality only
      name: isSameMunicipality ? searchedHnr.municipality.name : '',
      number: externalHnr.municipality.number,
    },
    farmNumber: externalHnr.farmNumber,
    mainNumber: externalHnr.mainNumber,
    property: externalHnr.property,
    hnr: externalHnr.hnr,
  }
}

/**
 * Creates a query config for searching properties, with optional external (matrikkel) lookup.
 * When `includeExternal` is true, maps the internal PropertyDetailsDto response to the
 * SearchPropertyResponse shape expected by the property search flow.
 *
 * The internal `/properties` lookup (`searchProperties`) is scoped to the exact holding number
 * searched for, so it can never return sibling sub-numbers on its own. Sibling properties under
 * the same main number are fetched separately from the external/Matrikkel endpoint
 * (`searchProperty`), which always returns the full main-number tree regardless of which holding
 * number was queried. This is best-effort: if the sibling lookup fails, the primary searched
 * property is still returned.
 */
export const searchPropertyWithExternalQuery = (params: SearchPropertyWithExternalParams) =>
  queryOptions({
    queryKey: [
      ...propertiesBaseKey,
      'search',
      {
        municipality: params.municipality,
        farmNumber: params.farmNumber,
        propertyNumber: params.propertyNumber,
        municipalityName: params.municipalityName ?? '',
        includeExternal: params.includeExternal ?? false,
      },
    ] as const,
    queryFn: async (): Promise<SearchPropertyResponse> => {
      if (params.includeExternal) {
        const result = await searchProperties({
          municipality: params.municipality,
          farmNumber: params.farmNumber,
          propertyNumber: params.propertyNumber,
          includeExternal: true,
        })
        if (result.properties.length === 0) {
          return { properties: [], hasMore: false }
        }
        const [searchedHnrProperty, searchedProperty] = mapDtoToProperties(
          result.properties[0],
          params,
        )
        let externalHnr: Property | undefined
        let siblingProperties: Property[] = []
        try {
          const externalResult = await searchProperty({
            municipality: params.municipality,
            farmNumber: params.farmNumber,
            propertyNumber: params.propertyNumber,
            municipalityName: params.municipalityName ?? '',
          })
          externalHnr = externalResult.properties.find((property) => property.isHnr)
          siblingProperties = externalResult.properties.filter(
            (property) =>
              !property.isHnr &&
              // A property only matches the already-fetched `searchedProperty` when both the
              // holding number AND the lease number line up. Comparing propertyNumber alone
              // would wrongly exclude lease-number siblings that share the same holding
              // number (e.g. 33/8 and 33/8/1 are distinct properties).
              !(
                property.propertyNumber === searchedProperty.propertyNumber &&
                (property.leaseNumber ?? '0') === '0'
              ),
          )
        } catch (error) {
          logger.warn('Failed to fetch sibling properties for main number', {
            municipality: params.municipality,
            farmNumber: params.farmNumber,
            propertyNumber: params.propertyNumber,
            error,
          })
        }

        return {
          properties: [
            withExternalRoot(searchedHnrProperty, externalHnr),
            searchedProperty,
            ...siblingProperties,
          ],
          hasMore: false,
        }
      }
      return searchProperty({
        municipality: params.municipality,
        farmNumber: params.farmNumber,
        propertyNumber: params.propertyNumber,
        municipalityName: params.municipalityName ?? '',
      })
    },
    enabled: false,
    retry: false,
  })

/**
 * Creates a query config for fetching a single forest owner by ID
 */
export const getForestOwnerQuery = (id: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: ['forestOwner', tenantId ?? 'VikenSkog', id] as const,
    queryFn: () => getForestOwner(id),
  })

/**
 * Creates a query config for fetching forest owners with TanStack Query
 * @param params Required query params: tenantId, pageSize, optional searchTerm, etc.
 */
export const getForestOwnerGroupsQuery = () =>
  queryOptions({
    queryKey: ['forestOwnerGroups'],
    queryFn: () => getForestOwnerGroups(),
  })

/**
 * Creates a query config for looking up the city name for a Norwegian postal code.
 */
export const getPostalCodeCityQuery = (postalCode: string) =>
  queryOptions({
    queryKey: ['postalCodeCity', postalCode] as const,
    queryFn: () => getPostalCodeCity(postalCode),
  })

/**
 * Creates a query config for fetching properties for a forest owner
 */
export const getForestOwnerPropertiesQuery = (params: GetPropertiesForForestOwnerParams) =>
  queryOptions({
    queryKey: queryKeys.forestOwnerProperties(
      params.forestOwnerId,
      params.filterCapitalContributing,
    ),
    queryFn: () => getPropertiesForForestOwner(params),
  })
/**
 * Creates a query config for fetching an organization by organization number
 */
export const getOrganizationQuery = (params: GetOrganizationParams) =>
  queryOptions({
    queryKey: ['organization', params.orgNumber],
    queryFn: () => getOrganization(params),
  })

/**
 * Creates a query config for searching organizations
 */
export const searchOrganizationsQuery = (params: SearchOrganizationParams) =>
  queryOptions({
    queryKey: [...organizationSearchBaseKey, params.searchTerm] as const,
    queryFn: () => searchOrganizations(params),
  })

/**
 * Creates a query config for searching properties
 */
const fetchSearchProperty = (p: SearchPropertyParams): Promise<SearchPropertyResponse> =>
  searchProperty(p)

type SearchPropertyKey = readonly [(typeof propertiesBaseKey)[0], 'search', SearchPropertyParams]
type SearchPropertyQueryOptions = QueryOptions<
  SearchPropertyResponse,
  Error,
  SearchPropertyResponse,
  SearchPropertyKey
>

export const searchPropertyQuery = (params: SearchPropertyParams): SearchPropertyQueryOptions =>
  queryOptions<SearchPropertyResponse, Error, SearchPropertyResponse, SearchPropertyKey>({
    queryKey: [...propertiesBaseKey, 'search', params] as const,
    queryFn: (): Promise<SearchPropertyResponse> => fetchSearchProperty(params),
  })

/**
 * Creates a query config for fetching property owners
 */
export const getPropertyOwnersQuery = ({
  municipalityNumber,
  farmNumber,
  holdingNumber,
  leaseNumber,
  sectionNumber,
}: {
  municipalityNumber: number
  farmNumber: number
  holdingNumber: number
  leaseNumber?: number
  sectionNumber?: number
}) =>
  queryOptions({
    queryKey: [
      'property',
      'owners',
      { municipalityNumber, farmNumber, holdingNumber, leaseNumber, sectionNumber },
    ] as const,
    queryFn: () =>
      getPropertyOwners({
        municipalityNumber,
        farmNumber,
        holdingNumber,
        leaseNumber,
        sectionNumber,
      }),
  })

/**
 * Creates a query config for verifying property ownership
 */
export const verifyOwnershipQuery = ({
  forestOwnerId,
  municipalityNumber,
  farmNumber,
  holdingNumber,
}: {
  forestOwnerId: string
  municipalityNumber: string
  farmNumber: string
  holdingNumber: string
}) =>
  queryOptions({
    queryKey: [
      'forestowners',
      forestOwnerId,
      'ownerships',
      'verify',
      { municipalityNumber, farmNumber, holdingNumber },
    ] as const,
    queryFn: () =>
      verifyPropertyOwnership({ forestOwnerId, municipalityNumber, farmNumber, holdingNumber }),
  })

export type ForrestOwnerSearch = z.infer<typeof forestOwnerSearchSchema>

// get details for specific property
export const getPropertyDetailsQuery = (propertyId: string) =>
  queryOptions({
    queryKey: queryKeys.propertyDetails(propertyId),
    queryFn: () => getProperty(propertyId),
  })

export const getCooperativeMembershipQuery = (params: GetCooperativeMembershipParams) =>
  queryOptions({
    queryKey: queryKeys.cooperativeMembership(params.tenantId ?? 'VikenSkog', params.forestOwnerId),
    queryFn: () => getCooperativeMembership(params),
  })

export const getPartnerCostPreviewQuery = (
  cooperativeMembershipId: string,
  partnerForestOwnerId: string,
) =>
  queryOptions({
    queryKey: queryKeys.partnerCostPreview(cooperativeMembershipId, partnerForestOwnerId),
    queryFn: () => getPartnerCostPreview(cooperativeMembershipId, partnerForestOwnerId),
  })

export const getPartnerSettlementPreviewQuery = (
  cooperativeMembershipId: string,
  partnerForestOwnerId: string,
  continuesAsMember = true,
) =>
  queryOptions({
    queryKey: queryKeys.partnerSettlementPreview(
      cooperativeMembershipId,
      partnerForestOwnerId,
      continuesAsMember,
    ),
    queryFn: () =>
      getPartnerSettlementPreview(cooperativeMembershipId, partnerForestOwnerId, continuesAsMember),
  })

/**
 * Creates a query config for fetching a shareholder by ID
 */
export const getShareholderQuery = (shareholderId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: [...shareholderBaseKey, tenantId ?? 'VikenSkog', shareholderId],
    queryFn: () => getShareholder(shareholderId),
  })

/**
 * Creates a query config for evaluating whether a cooperative membership
 * documentation warning should be shown for the given property/forest owner.
 */
export const getCooperativeMembershipWarningQuery = (
  propertyId: string,
  targetForestOwnerId: string,
) =>
  queryOptions({
    queryKey: queryKeys.cooperativeMembershipWarning(propertyId, targetForestOwnerId),
    queryFn: async () => {
      const evaluation = await evaluateCooperativeMembershipWarning({
        propertyId,
        targetForestOwnerId,
      })

      if (evaluation.action === 'error') {
        throw evaluation.error
      }

      return evaluation
    },
  })

export const getTimberBuyerQuery = (timberBuyerId: string) =>
  queryOptions({
    queryKey: ['timberBuyer', timberBuyerId],
    queryFn: () => getTimberBuyer(timberBuyerId),
    enabled: Boolean(timberBuyerId),
  })

export const getTimberBuyersQuery = (params: GetTimberBuyersParams) =>
  queryOptions({
    queryKey: ['timberBuyers', params],
    queryFn: () => getTimberBuyers(params),
  })

export const getSalesAgreementsQuery = (params: GetSalesAgreementsParams) =>
  queryOptions({
    queryKey: queryKeys.salesAgreements(params),
    queryFn: () => getSalesAgreements(params),
  })

export const getSalesAgreementQuery = (timberBuyerId: string, salesAgreementId: string) =>
  queryOptions({
    queryKey: queryKeys.salesAgreement(timberBuyerId, salesAgreementId),
    queryFn: () => getSalesAgreement(timberBuyerId, salesAgreementId),
    enabled: Boolean(timberBuyerId) && Boolean(salesAgreementId),
  })

export const getSalesAgreementPriceListsQuery = (params: GetSalesAgreementPriceListsParams) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementPriceLists(params),
    queryFn: () => getSalesAgreementPriceLists(params),
  })

export const getSalesAgreementPricePeriodsQuery = (params: GetSalesAgreementPricePeriodsParams) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementPricePeriods(params.timberBuyerId, params.salesAgreementId),
    queryFn: () => getSalesAgreementPricePeriods(params),
    enabled: Boolean(params.timberBuyerId) && Boolean(params.salesAgreementId),
  })

export const getSalesAgreementAssortmentCategoriesQuery = (
  params: GetSalesAgreementAssortmentCategoriesParams,
) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementAssortmentCategories(
      params.timberBuyerId,
      params.salesAgreementId,
      params.pricePeriodId,
      params.sort,
    ),
    queryFn: () => getSalesAgreementAssortmentCategories(params),
    enabled: Boolean(params.timberBuyerId && params.salesAgreementId && params.pricePeriodId),
  })

export const getSalesAgreementPricePeriodQuery = (params: GetSalesAgreementPricePeriodParams) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementPricePeriod(
      params.timberBuyerId,
      params.salesAgreementId,
      params.pricePeriodId,
    ),
    queryFn: () => getSalesAgreementPricePeriod(params),
    enabled: Boolean(params.timberBuyerId && params.salesAgreementId && params.pricePeriodId),
  })

export const getSalesAgreementPricePeriodZonePricesQuery = (params: ZonePricesParams) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementPricePeriodZonePrices(
      params.timberBuyerId,
      params.salesAgreementId,
      params.pricePeriodId,
    ),
    queryFn: () => getZonePrices(params),
    enabled: Boolean(params.timberBuyerId && params.salesAgreementId && params.pricePeriodId),
  })

export const getSalesAgreementAssortmentPricesQuery = (
  params: GetSalesAgreementAssortmentPricesParams,
) =>
  queryOptions({
    queryKey: queryKeys.salesAgreementAssortmentPrices(
      params.timberBuyerId,
      params.salesAgreementId,
      params.pricePeriodId,
    ),
    queryFn: () => getSalesAgreementAssortmentPrices(params),
    enabled: Boolean(params.timberBuyerId && params.salesAgreementId && params.pricePeriodId),
  })

export const propertiesBaseKey = ['properties'] as const
export const propertyDetailsBaseKey = ['propertyDetails'] as const
export const forestOwnerPropertiesBaseKey = ['forestOwnerProperties'] as const
export const forestOwnerPropertiesOwnerBaseKey = (forestOwnerId: string) =>
  [...forestOwnerPropertiesBaseKey, forestOwnerId] as const
export const historicalPropertiesBaseKey = (forestOwnerId: string) =>
  ['historicalProperties', forestOwnerId] as const
export const getTimberBuyerPricePeriodsQuery = (params: GetTimberBuyerPricePeriodsParams) =>
  queryOptions({
    queryKey: queryKeys.timberBuyerPricePeriods(params),
    queryFn: () => getTimberBuyerPricePeriods(params),
    enabled: Boolean(params.timberBuyerId),
  })

/**
 * Query key factories for cache invalidation
 */
export const queryKeys = {
  assortments: () => ['assortments'] as const,
  assortment: (id: string) => ['assortments', 'detail', id] as const,
  assortmentTypes: () => ['assortmentTypes'] as const,
  assortmentType: (id: string) => ['assortmentTypes', 'detail', id] as const,
  assortmentSyncStatus: () => ['assortmentSyncStatus'] as const,
  documents: (tenantId: TenantId, ownerId: string) => ['documents', tenantId, ownerId] as const,
  contractor: (contractorId: string) => ['contractor', contractorId] as const,
  contractorDocuments: (tenantId: TenantId, contractorId: string) =>
    ['contractorDocuments', tenantId, contractorId] as const,
  contractorMachines: (contractorId: string) => ['contractor', contractorId, 'machines'] as const,
  contractorMachine: (contractorId: string, machineId: string) =>
    ['contractor', contractorId, 'machines', machineId] as const,
  contractorEmployees: (contractorId: string, tenantId?: TenantId) =>
    ['contractor', contractorId, tenantId, 'employees'] as const,
  contractorCourses: (contractorId: string, tenantId?: TenantId) =>
    ['contractor', contractorId, tenantId, 'courses'] as const,
  contractorForestCrews: (contractorId: string, tenantId?: TenantId) =>
    ['contractor', contractorId, tenantId, 'forest-crews'] as const,
  contractorSmsHistory: (contractorId: string, tenantId?: TenantId) =>
    ['contractorSmsHistory', tenantId ?? 'VikenSkog', contractorId] as const,
  contractorSmsSenders: (contractorId: string, tenantId?: TenantId) =>
    ['contractorSmsSenders', tenantId ?? 'VikenSkog', contractorId] as const,
  contractors: (params: GetContractorsParams) => ['contractors', params] as const,
  contractorByOrganizationNumber: (tenantId: TenantId, organizationNumber: string) =>
    ['contractors', tenantId, 'organizationNumber', organizationNumber] as const,
  forestCrew: (contractorId: string, forestCrewId: string) =>
    ['contractor', contractorId, 'forest-crew', forestCrewId] as const,
  forestCrews: (params: GetForestCrewsParams) =>
    ['contractor', params.contractorId, 'forest-crews', params] as const,
  employee: (contractorId: string, employeeId: string, tenantId?: TenantId) =>
    ['contractor', contractorId, tenantId, 'employee', employeeId] as const,
  employeesBase: (contractorId: string) => ['contractor', contractorId, 'employees'] as const,
  employees: (params: GetEmployeesParams) =>
    ['contractor', params.contractorId, 'employees', params] as const,
  forestOwner: (tenantId: TenantId, forestOwnerId: string) =>
    ['forestOwner', tenantId, forestOwnerId] as const,
  forestOwnerNames: (forestOwnerIds: readonly string[] | undefined) =>
    ['forestOwnerNames', forestOwnerIds] as const,
  forestOwnerSuppliers: (forestOwnerId: string) => ['suppliers', forestOwnerId] as const,
  forestOwnerSupplier: (forestOwnerId: string) => ['supplier', forestOwnerId] as const,
  timberBuyer: (timberBuyerId: string) => ['timberBuyer', timberBuyerId] as const,
  timberBuyers: () => ['timberBuyers'] as const,
  salesAgreements: (params: GetSalesAgreementsParams) => ['salesAgreements', params] as const,
  salesAgreement: (timberBuyerId: string, salesAgreementId: string) =>
    ['salesAgreement', { timberBuyerId, salesAgreementId }] as const,
  salesAgreementPriceLists: (params: GetSalesAgreementPriceListsParams) =>
    ['salesAgreementPriceLists', params] as const,
  salesAgreementPricePeriods: (timberBuyerId: string, salesAgreementId: string) =>
    ['salesAgreementPricePeriods', { timberBuyerId, salesAgreementId }] as const,
  salesAgreementAssortmentCategories: (
    timberBuyerId: string,
    salesAgreementId: string,
    pricePeriodId: string,
    sort?: AssortmentCategorySort,
  ) =>
    [
      'salesAgreementAssortmentCategories',
      { timberBuyerId, salesAgreementId, pricePeriodId, ...(sort && { sort }) },
    ] as const,
  salesAgreementPricePeriod: (
    timberBuyerId: string,
    salesAgreementId: string,
    pricePeriodId: string,
  ) => ['salesAgreementPricePeriod', { timberBuyerId, salesAgreementId, pricePeriodId }] as const,
  salesAgreementPricePeriodZonePrices: (
    timberBuyerId: string,
    salesAgreementId: string,
    pricePeriodId: string,
  ) =>
    [
      'salesAgreementPricePeriodZonePrices',
      { timberBuyerId, salesAgreementId, pricePeriodId },
    ] as const,
  salesAgreementAssortmentPrices: (
    timberBuyerId: string,
    salesAgreementId: string,
    pricePeriodId: string,
  ) =>
    ['salesAgreementAssortmentPrices', { timberBuyerId, salesAgreementId, pricePeriodId }] as const,
  salesAgreementsByTimberBuyer: (timberBuyerId: string) =>
    ['salesAgreements', { timberBuyerId }] as const,
  timberBuyerPricePeriods: ({
    timberBuyerId,
    searchTerm,
    pageSize,
  }: GetTimberBuyerPricePeriodsParams) =>
    ['timberBuyerPricePeriods', { timberBuyerId, searchTerm, pageSize }] as const,
  // The unfiltered key is a prefix of the filtered key so invalidating it also invalidates filtered results.
  forestOwnerProperties: (forestOwnerId: string, filterCapitalContributing?: boolean) =>
    filterCapitalContributing
      ? ([...forestOwnerPropertiesOwnerBaseKey(forestOwnerId), true] as const)
      : forestOwnerPropertiesOwnerBaseKey(forestOwnerId),
  partnerCostPreview: (cooperativeMembershipId: string, partnerForestOwnerId: string) =>
    ['partnerCostPreview', cooperativeMembershipId, partnerForestOwnerId] as const,
  partnerSettlementPreview: (
    cooperativeMembershipId: string,
    partnerForestOwnerId: string,
    continuesAsMember = true,
  ) =>
    [
      'partnerSettlementPreview',
      cooperativeMembershipId,
      partnerForestOwnerId,
      continuesAsMember,
    ] as const,
  propertyDetails: (propertyId: string) => [...propertyDetailsBaseKey, propertyId] as const,
  msGraphProfile: () => ['msGraphProfile'] as const,
  userPermissions: () => ['userPermissions'] as const,
  cooperativeMembership: (tenantId: string, forestOwnerId: string) =>
    [...cooperativeMembershipBaseKey, tenantId, forestOwnerId] as const,
  timberBuyerDeliveryLocations: (tenantId: TenantId, timberBuyerId: string) =>
    ['timberBuyerDeliveryLocations', { tenantId, timberBuyerId }] as const,
  cooperativeMembershipWarning: (propertyId: string, targetForestOwnerId: string) =>
    ['cooperativeMembershipWarning', propertyId, targetForestOwnerId] as const,
}

export const getTimberBuyerDeliveryLocationsQuery = (
  params: GetTimberBuyerDeliveryLocationsParams,
) =>
  queryOptions({
    queryKey: queryKeys.timberBuyerDeliveryLocations(params.tenantId, params.timberBuyerId),
    queryFn: () => getTimberBuyerDeliveryLocations(params),
  })

export const getContractorQuery = (contractorId: string) =>
  queryOptions({
    queryKey: queryKeys.contractor(contractorId),
    queryFn: () => getContractor(contractorId),
    enabled: Boolean(contractorId),
  })

export const contractorsBaseKey = ['contractors'] as const

export const getContractorsQuery = (params: GetContractorsParams) =>
  queryOptions({
    queryKey: queryKeys.contractors(params),
    queryFn: () => getContractors(params),
  })

export const getContractorByOrganizationNumberQuery = (
  tenantId: TenantId,
  organizationNumber: string,
) =>
  queryOptions({
    queryKey: queryKeys.contractorByOrganizationNumber(tenantId, organizationNumber),
    queryFn: () => findContractorByOrganizationNumber(tenantId, organizationNumber),
  })

export const getContractorEmployeesQuery = (contractorId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: queryKeys.contractorEmployees(contractorId, tenantId),
    queryFn: () => {
      if (!tenantId) throw new Error('Missing tenantId for contractor employees query')
      return getContractorEmployees(contractorId, tenantId)
    },
    enabled: Boolean(contractorId && tenantId),
  })

export const getContractorMachinesQuery = (params: GetContractorMachinesParams) =>
  queryOptions({
    queryKey: [
      ...queryKeys.contractorMachines(params.contractorId),
      {
        tenantId: params.tenantId,
        status: params.status,
        searchTerm: params.searchTerm,
        sort: params.sort,
        pageSize: params.pageSize,
        continuationToken: params.continuationToken,
      },
    ] as const,
    queryFn: () => getContractorMachines(params),
    enabled: Boolean(params.contractorId && params.tenantId),
  })

export const getContractorMachineQuery = (contractorId: string, machineId: string) =>
  queryOptions({
    queryKey: queryKeys.contractorMachine(contractorId, machineId),
    queryFn: () => getContractorMachine(contractorId, machineId),
    enabled: Boolean(contractorId && machineId),
  })

export const getCoursesQuery = (contractorId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: queryKeys.contractorCourses(contractorId, tenantId),
    queryFn: () => getCourses({ contractorId, tenantId }),
    enabled: Boolean(contractorId),
  })

export const getContractorForestCrewsQuery = (contractorId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: queryKeys.contractorForestCrews(contractorId, tenantId),
    queryFn: () => {
      if (!tenantId) throw new Error('Missing tenantId for contractor forest crews query')
      return getContractorForestCrews(contractorId, tenantId)
    },
    enabled: Boolean(contractorId && tenantId),
  })

export const getMachineBrandsQuery = () =>
  queryOptions({
    queryKey: ['machineBrands'] as const,
    queryFn: () => getMachineBrands(),
  })

export const getContractorSmsHistoryQuery = (params: GetContractorSmsHistoryParams) =>
  queryOptions({
    queryKey: [
      ...queryKeys.contractorSmsHistory(params.contractorId, params.tenantId),
      params.pageSize,
      params.continuationToken,
      params.dateFrom,
      params.dateTo,
      params.sort,
      params.senderName,
    ],
    queryFn: () => getContractorSmsHistory(params),
  })

export const getContractorSmsSendersQuery = (contractorId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: queryKeys.contractorSmsSenders(contractorId, tenantId),
    queryFn: () => getContractorSmsSenders(contractorId, tenantId),
  })

export const getForestCrewQuery = (contractorId: string, forestCrewId: string) =>
  queryOptions({
    queryKey: queryKeys.forestCrew(contractorId, forestCrewId),
    queryFn: () => getForestCrew(contractorId, forestCrewId),
    enabled: Boolean(contractorId && forestCrewId),
  })

export const getForestCrewsQuery = (params: GetForestCrewsParams) =>
  queryOptions({
    queryKey: queryKeys.forestCrews(params),
    queryFn: () => getForestCrews(params),
    enabled: Boolean(params.contractorId),
  })

export const getEmployeeQuery = (contractorId: string, employeeId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: queryKeys.employee(contractorId, employeeId, tenantId),
    queryFn: () => {
      if (!tenantId) throw new Error('Missing tenantId for employee query')
      return getEmployee(contractorId, employeeId, tenantId)
    },
    enabled: Boolean(contractorId && employeeId && tenantId),
  })

export const getEmployeesQuery = (params: GetEmployeesParams) =>
  queryOptions({
    queryKey: queryKeys.employees(params),
    queryFn: () => getEmployees(params),
    enabled: Boolean(params.contractorId),
  })

export const getMunicipalitiesQuery = () =>
  queryOptions({
    queryKey: ['municipalities'],
    queryFn: async () => {
      const municipalities = await getMunicipalities()
      return municipalities.sort((a, b) =>
        a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }),
      )
    },
  })

export const getTransportPayersQuery = () =>
  queryOptions({
    queryKey: ['transportPayers'],
    queryFn: async () => {
      const payers = await getTransportPayers()
      return payers.sort((a, b) => a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }))
    },
  })

export const getLineOfBusinessQuery = () =>
  queryOptions({
    queryKey: ['lineOfBusiness'],
    queryFn: async () => {
      const items = await getLinesOfBusiness()
      return items.sort((a, b) => a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }))
    },
  })

export const getAccountManagersQuery = () =>
  queryOptions({
    queryKey: ['accountManagers'],
    queryFn: async () => {
      const employees = await getAccountManagers()
      return employees.sort((a, b) =>
        a.displayName.localeCompare(b.displayName, 'no', { sensitivity: 'base' }),
      )
    },
  })

export const getTransportResponsiblesQuery = () =>
  queryOptions({
    queryKey: ['transportResponsibles'],
    queryFn: async () => {
      const responsibles = await getTransportResponsibles()
      return responsibles.sort((a, b) =>
        a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }),
      )
    },
  })

export const getMeasurementOrganizationsQuery = () =>
  queryOptions({
    queryKey: ['measurementOrganizations'],
    queryFn: async () => {
      const orgs = await getMeasurementOrganizations()
      return orgs.sort((a, b) => a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }))
    },
  })

/**
 * Creates a query config for fetching additional info lines for a forest owner
 */
export const getAdditionalInfoLinesQuery = (forestOwnerId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: ['additionalInfoLines', tenantId ?? 'VikenSkog', forestOwnerId],
    queryFn: () => getAdditionalInfoLines(forestOwnerId),
  })

/**
 * Creates a query config for fetching all additional info types
 */
export const getAdditionalInfoTypesQuery = (tenantId?: TenantId) =>
  queryOptions({
    queryKey: ['additionalInfoTypes', tenantId ?? 'VikenSkog'],
    queryFn: () => getAdditionalInfoTypes(),
  })

export const getForestOwnerHistoricalEventsQuery = (params: GetHistoricalEventsParams) =>
  queryOptions({
    queryKey: [
      'forestOwnerHistoricalEvents',
      params.forestOwnerId,
      params.pageSize,
      params.continuationToken,
      params.dateFrom,
      params.dateTo,
      params.sort,
      params.actions,
    ],
    queryFn: () => getForestOwnerHistoricalEvents(params),
  })

export const forestOwnersBaseKey = ['forestOwners'] as const
export const shareholderBaseKey = ['shareholder'] as const
export const cooperativeMembershipBaseKey = ['cooperativeMembership'] as const

export const smsHistoryBaseKey = ['smsHistory'] as const

/**
 * Creates a query config for fetching SMS history for a forest owner
 */
export const getSmsHistoryQuery = (params: GetSmsHistoryParams) =>
  queryOptions({
    queryKey: [
      ...smsHistoryBaseKey,
      params.forestOwnerId,
      params.pageSize,
      params.continuationToken,
      params.dateFrom,
      params.dateTo,
      params.sort,
      params.senderName,
    ],
    queryFn: () => getSmsHistory(params),
  })

/**
 * Creates a query config for fetching SMS senders for a forest owner
 */
export const getSmsSendersQuery = (forestOwnerId: string, tenantId?: TenantId) =>
  queryOptions({
    queryKey: ['smsSenders', tenantId ?? 'VikenSkog', forestOwnerId],
    queryFn: () => getSmsSenders(forestOwnerId, tenantId),
  })

/**
 * Creates a query config for fetching historical properties for a forest owner
 */
export const getHistoricalPropertiesQuery = (params: GetHistoricalPropertiesParams) =>
  queryOptions({
    queryKey: [
      ...historicalPropertiesBaseKey(params.forestOwnerId),
      params.pageSize,
      params.continuationToken,
    ],
    queryFn: () => getHistoricalProperties(params),
  })

/**
 * Creates a query config for fetching suppliers with municipality for an organization
 */
export const getSuppliersQuery = (params: GetSuppliersParams) =>
  queryOptions({
    queryKey: ['organizationSuppliers', params.organizationNumber],
    queryFn: () => getSuppliersWithMunicipality(params),
    enabled: Boolean(params.organizationNumber),
  })

export const documentsBaseKey = (tenantId: TenantId, ownerId: string) =>
  ['documents', tenantId, ownerId] as const

/**
 * Creates a query config for fetching documents for a forest owner
 */
export const getDocumentsQuery = (params: GetDocumentsParams) =>
  queryOptions({
    queryKey: [
      ...documentsBaseKey(params.tenantId ?? getCurrentTenant(), params.ownerId),
      params.pageSize,
      params.continuationToken,
      params.sort,
      params.dateFrom,
      params.dateTo,
      params.documentType,
    ],
    queryFn: () => getDocuments(params),
  })

/**
 * Creates a query config for fetching documents for a contractor
 */
export const getContractorDocumentsQuery = (params: GetContractorDocumentsParams) =>
  queryOptions({
    queryKey: [
      ...queryKeys.contractorDocuments(params.tenantId ?? getCurrentTenant(), params.ownerId),
      params.pageSize,
      params.continuationToken,
      params.sort,
      params.dateFrom,
      params.dateTo,
      params.documentType,
      params.status,
    ],
    queryFn: () => getContractorDocuments(params),
  })

/**
 * Creates a query config for fetching suppliers for a forest owner
 */
export const getForestOwnerSuppliersQuery = (forestOwnerId: string) =>
  queryOptions({
    queryKey: ['suppliers', forestOwnerId],
    queryFn: () => getSuppliers(forestOwnerId),
    enabled: Boolean(forestOwnerId),
  })

/**
 * Creates a query config for fetching a specific supplier for a forest owner
 */
export const getForestOwnerSupplierQuery = (params: GetForestOwnerSupplierParams) =>
  queryOptions({
    queryKey: ['supplier', params.forestOwnerId, params.supplierId],
    queryFn: () => getForestOwnerSupplier(params),
    enabled: Boolean(params.forestOwnerId) && Boolean(params.supplierId),
  })

/**
 * Creates a query config for fetching all employee offices
 */
export const getOfficesQuery = () =>
  queryOptions({
    queryKey: ['offices'],
    queryFn: async () => {
      const offices = await getOffices()
      return offices.sort((a, b) => a.localeCompare(b, 'no', { sensitivity: 'base' }))
    },
  })

/**
 * Creates a query config for fetching company information for an organization
 */
export const getCompanyQuery = (organizationNumber: string) =>
  queryOptions({
    queryKey: ['company', organizationNumber],
    queryFn: () => getCompany(organizationNumber),
    enabled: Boolean(organizationNumber),
    retry: false,
  })
